"use client";

import type { RemixiconComponentType } from "@remixicon/react";
import { RiCloseLine } from "@remixicon/react";
import { Card, CardContent } from "@workspace/ui/components/card";

interface ArtistEmptyStateProps {
	icon: RemixiconComponentType;
	title: string;
	description: string;
}

export function ArtistEmptyState({
	icon: Icon,
	title,
	description,
}: ArtistEmptyStateProps) {
	return (
		<Card>
			<CardContent className="p-8 text-center">
				<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
					<Icon className="h-6 w-6 text-muted-foreground" />
				</div>
				<h3 className="mb-1 font-semibold text-xl">{title}</h3>
				<p className="text-muted-foreground text-sm">{description}</p>
			</CardContent>
		</Card>
	);
}

interface ArtistSearchEmptyStateProps {
	searchTerm: string;
	selectedYear: string;
	contentType: "festivals" | "sets" | "shows";
	artistName: string;
	onClearSearch: () => void;
	onClearYear: () => void;
}

export function ArtistSearchEmptyState({
	searchTerm,
	selectedYear,
	contentType,
	artistName,
	onClearSearch,
	onClearYear,
}: ArtistSearchEmptyStateProps) {
	const hasSearch = searchTerm.trim() !== "";
	const hasYear = selectedYear !== "all";

	const getEmptyTitle = () => {
		if (hasSearch && hasYear) {
			return `No ${contentType} found for "${searchTerm}" in ${selectedYear}`;
		}
		if (hasSearch) {
			return `No ${contentType} found for "${searchTerm}"`;
		}
		if (hasYear) {
			return `${artistName} didn't perform any ${contentType} in ${selectedYear}`;
		}
		return `No ${contentType} found`;
	};

	const getEmptyDescription = () => {
		if (hasSearch && hasYear) {
			return "Try adjusting your search or year filter";
		}
		if (hasSearch) {
			return "Try a different search term";
		}
		if (hasYear) {
			return "Try selecting a different year";
		}
		return "Try adjusting your filters";
	};

	return (
		<Card className="p-8 text-center">
			<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
				<RiCloseLine className="h-6 w-6 text-muted-foreground" />
			</div>
			<h3 className="mb-1 font-semibold text-xl">{getEmptyTitle()}</h3>
			<p className="mb-4 text-muted-foreground text-sm">
				{getEmptyDescription()}
			</p>

			{(hasSearch || hasYear) && (
				<div className="mt-4 flex justify-center gap-2">
					{hasSearch && (
						<button
							type="button"
							onClick={onClearSearch}
							className="rounded-lg bg-muted px-3 py-2 text-sm transition-colors hover:bg-muted/80"
						>
							Clear Search
						</button>
					)}
					{hasYear && (
						<button
							type="button"
							onClick={onClearYear}
							className="rounded-lg bg-muted px-3 py-2 text-sm transition-colors hover:bg-muted/80"
						>
							Show All Years
						</button>
					)}
				</div>
			)}
		</Card>
	);
}

// Export the SearchEmptyState component that was moved from empty-states folder
export { SearchEmptyState } from "./search-empty-state";
